import React, { useRef, useState } from 'react';

import { css } from '@emotion/css';

import { Icons } from '@/assets/icons';
import { useStyles } from '@/hooks/useStyles';
import { FileModel, UploadedFileType } from '@/models/FileModel';
import { Patient } from '@/models/PatientModel';
import { Queries } from '@/queries/Queries';
import { Theme } from '@/theme/CreateTheme.ts';
import { Button } from '@/uiKit/atoms/Button.tsx';

import { Loader } from '../loading/Loader';

type UploadButtonProps = {
  id?: string;
  onFileChange: (file?: FileModel) => void;
  fileType: UploadedFileType;
  patientInfo: Pick<Patient, 'givenName' | 'familyName'>;
  button?: {
    variant?: 'text' | 'outlined' | 'contained';
    text?: string;
  };
  defaultValue?: FileModel;
  hasTrash?: boolean;
  displayFilename?: boolean;
  className?: string;
  buttonClassName?: string;
};

export const UploadButton = ({
  id,
  onFileChange,
  fileType,
  patientInfo,
  defaultValue,
  button,
  hasTrash = false,
  displayFilename = true,
  className,
  buttonClassName,
}: UploadButtonProps) => {
  const styles = useStyles(makeStyles);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFile = Queries.practitioner.useUploadFile();

  const [hasBeenDeleted, setHasBeenDeleted] = useState(false);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const uploadFileHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }
    setHasBeenDeleted(false);

    const file = event.target.files[0];
    uploadFile.mutate(
      {
        file,
        type: fileType,
        patient_firstname: patientInfo.givenName,
        patient_lastname: patientInfo.familyName,
      },
      {
        onSuccess: data => {
          onFileChange(data);
        },
      },
    );
  };

  const filename =
    hasBeenDeleted || !displayFilename
      ? null
      : fileInputRef.current?.files?.[0]?.name || defaultValue?.name;

  const Icon = () =>
    uploadFile.status == 'pending' ? <Loader size="S" /> : <Icons.fileUpload />;

  return (
    <div id={id} className={className} data-testid="upload-button">
      <Button
        variant={button?.variant ?? 'outlined'}
        tabIndex={-1}
        onClick={handleButtonClick}
        endIcon={filename && !button?.text && <Icon />}
        className={buttonClassName}
      >
        {filename ? filename : button?.text ? button.text : <Icon />}
      </Button>
      <input
        type="file"
        className={styles.hidden}
        ref={fileInputRef}
        onChange={uploadFileHandler}
      />
      {hasTrash && filename && (
        <Button
          variant="text"
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            setHasBeenDeleted(true);
            onFileChange({
              name: '',
              url: '',
              path: '',
            });
          }}
          startIcon={<Icons.trash color="black" />}
        />
      )}
    </div>
  );
};

const makeStyles = (_theme: Theme) => ({
  hidden: css`
    display: none;
  `,
});
